import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, ArrowLeft, MessageCircle, ShoppingBag, Search, Flag } from "lucide-react";
import ReportDialog from "../components/ReportDialog";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Avatar from "../components/social/Avatar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    const { data } = await api.get("/threads");
    setThreads(data);
    return data;
  }, []);

  const openThread = useCallback(async (t) => {
    const { data } = await api.get(`/threads/${t.thread_id}`);
    setActive(data);
    setParams({ t: t.thread_id }, { replace: true });
    loadThreads();
  }, [loadThreads, setParams]);

  useEffect(() => {
    (async () => {
      const list = await loadThreads();
      const tid = params.get("t");
      if (tid) {
        try { const { data } = await api.get(`/threads/${tid}`); setActive(data); }
        catch (e) { console.error("open thread failed", e); }
      } else if (list.length && window.innerWidth >= 1024) {
        openThread(list[0]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll the active conversation
  useEffect(() => {
    if (!active) return;
    const id = setInterval(async () => {
      try { const { data } = await api.get(`/threads/${active.thread_id}`); setActive(data); loadThreads(); }
      catch (e) { console.error("poll failed", e); }
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.thread_id]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [active?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    const { data } = await api.post(`/threads/${active.thread_id}/messages`, { text });
    setActive((a) => ({ ...a, messages: [...(a.messages || []), data] }));
    setText("");
    loadThreads();
  };

  const filtered = threads.filter((t) =>
    !q.trim() || t.other_name?.toLowerCase().includes(q.toLowerCase()) || t.listing_title?.toLowerCase().includes(q.toLowerCase()));

  const ThreadAvatar = ({ t, size = 48 }) => (
    t.kind === "listing" && t.listing_image
      ? <img src={t.listing_image} alt="" className="rounded-xl object-cover" style={{ width: size, height: size }} />
      : <Avatar user={{ name: t.other_name, picture: t.other_picture }} size={size} />
  );

  const List = (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg overflow-hidden flex flex-col" data-testid="messages-list">
      <div className="p-4 border-b border-slate-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="messages-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats…" className="rounded-full h-10 pl-10" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <p className="text-sm text-slate-400 py-10 px-4 text-center">No conversations yet. Message a friend from the Playground, or a seller from the Market.</p>
        )}
        {filtered.map((t) => (
          <button key={t.thread_id} data-testid={`thread-${t.thread_id}`} onClick={() => openThread(t)}
            className={`w-full flex items-center gap-3 p-3 text-left transition border-l-4 ${active?.thread_id === t.thread_id ? "bg-slate-50 border-[#9B5DE5]" : "border-transparent hover:bg-slate-50"}`}>
            <ThreadAvatar t={t} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-slate-800 text-sm truncate">{t.other_name}</span>
                {t.kind === "listing" ? <ShoppingBag className="h-3 w-3 text-amber-500 shrink-0" title="Marketplace" />
                  : <MessageCircle className="h-3 w-3 text-[#2AB7CA] shrink-0" title="Playground" />}
                <span className="ml-auto text-[11px] text-slate-400 shrink-0">{timeAgo(t.updated_at)}</span>
              </div>
              <div className="text-xs text-slate-400 truncate">
                {t.kind === "listing" && t.listing_title ? `${t.listing_title} · ` : ""}{t.last_text || "Say hi! 👋"}
              </div>
            </div>
            {t.unread > 0 && <span data-testid={`thread-unread-${t.thread_id}`} className="min-w-5 h-5 px-1 rounded-full bg-[#FF5A79] text-white text-xs font-bold flex items-center justify-center shrink-0">{t.unread}</span>}
          </button>
        ))}
      </div>
    </div>
  );

  const Conversation = active && (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg overflow-hidden flex flex-col" data-testid="conversation">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <button onClick={() => { setActive(null); setParams({}, { replace: true }); }} data-testid="conv-back-btn"
          className="lg:hidden h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><ArrowLeft className="h-4 w-4" /></button>
        <ThreadAvatar t={active} size={40} />
        <div className="min-w-0">
          <div className="font-bold text-slate-800" data-testid="conv-title">{active.other_name}</div>
          {active.kind === "listing" && active.listing_title && (
            <div className="text-xs text-slate-400 truncate">About: {active.listing_title}</div>
          )}
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 min-h-[50vh] max-h-[60vh]" data-testid="conv-messages">
        {(active.messages || []).length === 0 && <p className="text-center text-slate-400 text-sm py-10">No messages yet — say hello! 👋</p>}
        {(active.messages || []).map((m) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div key={m.id} className={`group flex items-center gap-1.5 ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#9B5DE5] text-white" : "bg-slate-100 text-slate-700"}`}>{m.text}</div>
              {!mine && (
                <button onClick={() => setReportTarget({ type: "message", id: m.id, threadId: active.thread_id })} title="Report message"
                  data-testid={`report-message-${m.id}`}
                  className="opacity-40 hover:opacity-100 h-7 w-7 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-500 flex items-center justify-center transition-opacity">
                  <Flag className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="p-3 flex gap-2 border-t border-slate-100">
        <Input data-testid="conv-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="rounded-full h-11" />
        <Button type="submit" size="icon" data-testid="conv-send-btn" className="rounded-full h-11 w-11 bg-[#9B5DE5] hover:bg-[#843fd6] shrink-0"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-5">
          <h1 className="font-display font-bold text-3xl text-slate-900">Messages</h1>
          <MessageCircle className="h-6 w-6 text-[#9B5DE5]" />
        </div>
        {/* Desktop: two-pane. Mobile: list OR conversation */}
        {isDesktop ? (
          <div className="grid grid-cols-[340px_1fr] gap-6 items-start">
            {List}
            {active ? Conversation : (
              <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-16 text-center text-slate-400" data-testid="conversation-empty">
                <MessageCircle className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                Pick a conversation to start chatting.
              </div>
            )}
          </div>
        ) : (
          <div>{active ? Conversation : List}</div>
        )}
      </div>
      <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />
    </div>
  );
}
